import React from 'react';
import PropTypes from 'prop-types';
import combineReducers from '@americanexpress/vitruvius/immutable';
import products from 'axp-product-ducks';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import { OneFetchyeProvider, OneCache } from 'fetchye-one-app';
import { RenderModule, composeModules, loadModule } from 'holocron';
import { fromJS } from 'immutable';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { experimentConfigReducer } from '@americanexpress/one-xp-client-sdk';
import { Helmet } from 'react-helmet';
import childRoutes from '../childRoutes';
import { splitLocale } from '../utils/setActiveLocale';

const AxpShopRoot = ({ children, languageData: { seoFallbacks }, locale }) => {
  const { lang, country } = splitLocale(locale);
  return (
    <>
      <Helmet
        meta={[
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        ]}
        link={[{ rel: 'icon', href: 'https://www.americanexpress.com/favicon.ico' }]}
        htmlAttributes={{
          lang,
          country,
        }}
      />
      <RenderModule moduleName="axp-one-seo" props={{ fallbacks: seoFallbacks }} />
      <OneFetchyeProvider>{children}</OneFetchyeProvider>
    </>
  );
};

export const TestableAxpShopRoot = AxpShopRoot;

AxpShopRoot.propTypes = {
  children: PropTypes.shape({}),
  locale: PropTypes.string,
  languageData: PropTypes.shape({
    applicationName: PropTypes.string,
    seoFallbacks: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      openGraph: PropTypes.shape({
        type: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
      }),
    }),
  }),
};

// Read about childRoutes:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/Routing.md#childroutes
AxpShopRoot.childRoutes = childRoutes;

// Read about appConfig:
// https://github.com/americanexpress/one-app/blob/main/docs/api/modules/App-Configuration.md
/* istanbul ignore next */
if (!global.BROWSER) {
  // eslint-disable-next-line global-require -- require needs to be inside browser check
  AxpShopRoot.appConfig = require('../config').default;
}

const logModuleLoadError = (error) => {
  console.error(`Error Loading Module ${error}`);
};

export const loadModuleData = async ({ store: { dispatch } }) => {
  try {
    await dispatch(composeModules([{ name: 'axp-one-seo' }]));
  } catch (e) {
    logModuleLoadError(e);
  }

  try {
    await dispatch(
      loadLanguagePack('axp-shop-root', {
        fallbackLocale: 'en-US',
      })
    );
  } catch (e) {
    logModuleLoadError(e);
  }

  try {
    await dispatch(composeModules([{ name: 'axp-error-message' }]));
  } catch (e) {
    logModuleLoadError(e);
  }

  // Changing to LoadModule to force client-side render of axp-global-header
  // and axp-footer on all tenant applications
  try {
    await dispatch(loadModule('axp-global-header'));
  } catch (e) {
    logModuleLoadError(e);
  }

  try {
    await dispatch(loadModule('axp-footer'));
  } catch (e) {
    logModuleLoadError(e);
  }
};

export const reqReducer = (state = fromJS({})) => state;
reqReducer.buildInitialState = ({ req } = {}) => req;

AxpShopRoot.holocron = {
  name: 'axp-shop-root',
  loadModuleData,
  reducer: combineReducers({
    fetchye: OneCache().reducer,
    products,
    reqReducer,
    experimentConfig: experimentConfigReducer,
  }),
};

export default oneAppModuleWrapper('axp-shop-root')(AxpShopRoot);
